import { useRef } from 'react'
import { FOCUS_VISIBLE_RING, INPUT_FOCUS_RING } from '../../../lib/a11yFocus'
import { FORM_DISABLED_TEXT_CLASS, FORM_INPUT_TEXT_CLASS, FORM_LABEL_CLASS } from '../../../lib/formStyles'

type Validation = {
	required?: boolean
	min?: Number
	max?: Number
	minLength?: Number
	maxLength?: Number
	pattern?: RegExp
}

type InputProps = {
	register?: any
	errors?: any
	type: 'text' | 'number' | 'date' | 'email'
	name: string
	defaultValue?: string | number
	label?: React.ReactNode
	placeholder?: string
	disabled?: boolean
	validation?: Validation
	patternErrorMessage?: string
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
	onBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void
	onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
	additionalClass?: string
	inputAdditionalClass?: string
	value?: string
}

function Input({
	register = () => { },
	errors,
	type,
	name,
	defaultValue,
	label,
	placeholder,
	disabled,
	validation,
	patternErrorMessage,
	onChange,
	onBlur,
	onKeyDown,
	additionalClass,
	inputAdditionalClass,
	value,
}: InputProps) {
	const { ref: registeredRef, ...registeredProps } = register(name, validation) || {}
	const inputRef = useRef<HTMLInputElement | null>(null)
	const calendarButtonRef = useRef<HTMLButtonElement | null>(null)

	return (
		<div className={`${additionalClass && additionalClass}`}>
			{label && (
				<label htmlFor={name} className={`block ${FORM_LABEL_CLASS} my-1`}>
					{label}
				</label>
			)}
			{/* For date fields, THIS div is the only visible rounded surface (bg + border + shadow) -
			the real input is fully transparent (see below), so there's a single source of truth for
			the corner radius instead of two independently-rounded boxes drifting out of sync. */}
			<div className={`relative rounded shadow-sm ${type === 'date' ? `${disabled ? 'bg-charcoal-050/75 border border-charcoal-500' : 'bg-white border border-transparent'} focus-within:ring-2 focus-within:ring-algae-500 focus-within:border-algae-500` : ''}`}>
				{/* The native date-picker icon/spinner cluster is clipped off (the input below is
				deliberately widened to push it past the visible edge) rather than styled directly, since
				Chrome restricts most CSS on `::-webkit-calendar-picker-indicator` and Firefox exposes no
				equivalent hook. `overflow-clip` (not `overflow-hidden`) is required here: `hidden` creates
				a scroll container, and Tab-focusing the clipped native icon makes the browser auto-scroll
				it back into view, un-clipping it alongside our FA button; `clip` has no scrollport so
				there's nothing for the browser to scroll. */}
				<div className={`rounded ${type === 'date' ? '[overflow:clip]' : ''}`}>
					<input
						ref={(el) => {
							inputRef.current = el
							if (typeof registeredRef === 'function') {
								registeredRef(el)
							}
						}}
						// @tailwindcss/forms draws its own blue border/ring on `:focus` with higher
						// selector specificity than a plain (non-focus) `border-transparent` utility,
						// so cancelling it here needs the matching `focus:` variants, not just the base one.
						// bg-transparent: the wrapper above paints the visible surface - this avoids a second,
						// independently-rounded white box whose corners could drift out of sync with the ring.
						className={`block w-full rounded appearance-none ${FORM_INPUT_TEXT_CLASS} ${type === 'date' ? 'focus:outline-none focus:ring-0 focus:shadow-none focus:border-transparent border-transparent' : INPUT_FOCUS_RING} ${errors && errors[name]
							? 'border-error-500 bg-error-050 text-error-800 focus:border-error-500 focus:ring-error-500'
							: type === 'date'
								? 'text-charcoal-800 bg-transparent'
								: 'text-charcoal-800'
						} ${disabled
							? type === 'date'
								// The wrapper paints the disabled surface and border for date fields.
								? FORM_DISABLED_TEXT_CLASS
								: `border-charcoal-500 bg-charcoal-050/75 ${FORM_DISABLED_TEXT_CLASS} focus:border-charcoal-500 focus:ring-0`
								: ''
							} ${inputAdditionalClass || ''}`}
						style={type === 'date' ? { width: 'calc(100% + 48px)' } : undefined}
						type={type}
						placeholder={placeholder}
						disabled={!!disabled}
						defaultValue={defaultValue}
						onChange={(e) => onChange && onChange(e)}
						onBlur={(e) => onBlur && onBlur(e)}
						onKeyDown={(e) => {
							// Tab straight to the button, bypassing whatever invisible native control
							// (Chrome's clear button, etc.) the browser would otherwise focus first.
							if (type === 'date' && e.key === 'Tab' && !e.shiftKey) {
								e.preventDefault()
								calendarButtonRef.current?.focus()
							}
							onKeyDown && onKeyDown(e)
						}}
						value={value}
						{...registeredProps}
					/>
				</div>

				{/* CANONICAL date picker icon: mirrors SearchInput's divider + icon-button markup
				(same positioning, hover and focus-visible-ring classes) so every date field in the
				app matches the search/dropdown affordance exactly. Siblings of the clipped div above,
				not inside it, so their own focus rings are never clipped. */}
				{type === 'date' && (
					<>
						<div
							className='absolute top-2 bottom-2 w-px bg-charcoal-100 pointer-events-none'
							style={{ right: '40px' }}
						/>
						{/* tabIndex intentionally left at its default (0, not -1): the calendar icon
						must be its own reachable Tab stop after dd/mm/yyyy so keyboard users can see
						(and trigger) it, unlike SearchInput's button which is mouse-only. */}
						<button
							type='button'
							ref={calendarButtonRef}
							disabled={!!disabled}
							aria-label='Open calendar'
							onClick={() => (inputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null)?.showPicker?.()}
							onKeyDown={(e) => {
								// Shift+Tab straight back to the input, for the same reason as above.
								if (e.key === 'Tab' && e.shiftKey) {
									e.preventDefault()
									inputRef.current?.focus()
								}
							}}
							// focus: (not just hover:) keeps the active-state background visible while the
							// native date picker is open, since clicking leaves this button focused throughout.
							className={`absolute top-1/2 transform -translate-y-1/2 right-1 inline-flex items-center justify-center rounded text-charcoal-500 hover:bg-algae-050 focus:bg-algae-050 disabled:bg-transparent disabled:text-charcoal-500 h-9 w-9 ${FOCUS_VISIBLE_RING}`}
							style={{ right: '2.5px' }}
						>
							<i className='fa-regular fa-calendar' aria-hidden='true' />
						</button>
					</>
				)}

				{/* errors will return when field validation fails  */}
			</div>
			{errors && errors[name] && (
				<p className='mt-2 text-sm text-error-700'>
					{errors[name].type === 'required' && 'This field is required'}
					{errors[name].type === 'min' && `This value must be higher than ${validation?.min}`}
					{errors[name].type === 'max' && `This value must be lower than ${validation?.max}`}
					{errors[name].type === 'minLength' &&
						`Please enter at least ${validation?.minLength} characters`}
					{errors[name].type === 'maxLength' &&
						`Please enter fewer than ${validation?.maxLength} characters`}
					{errors[name].type === 'pattern' &&
						`${patternErrorMessage
							? patternErrorMessage
							: 'This field does not match the required pattern'
						}`}
				</p>
			)}
		</div>
	)
}
export default Input
